import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  BusinessEntity,
  EntityClassification,
  EntityInput,
  EntityPnL,
  FinanceDocument,
  FinancialAccount,
  Form1099Record,
  ReturnScheduleItem,
  ReturnStatus,
} from '../../types/family-finance'
import { CLASSIFICATION_LABELS } from '../../types/family-finance'
import {
  createEntity,
  list1099s,
  listAccounts,
  listDocuments,
  listEntities,
  listEntityRecords,
  listPnL,
  listReturnSchedule,
  updateEntityClassification,
  updateReturnStatus,
} from '../../lib/family-finance-api'
import EntityWorkspace from './EntityWorkspace'
import {
  Card,
  Chip,
  LoadError,
  LoadingRows,
  daysUntil,
  errorMessage,
  inputClass,
  money,
  primaryBtn,
  secondaryBtn,
  shortDate,
  signedMoney,
} from './shared'
import type { ChipTone } from './shared'

const RETURN_STATUS_META: Record<ReturnStatus, { label: string; tone: ChipTone }> = {
  'not-started': { label: 'Not started', tone: 'slate' },
  'in-progress': { label: 'In progress', tone: 'blue' },
  'extension-filed': { label: 'On extension', tone: 'amber' },
  filed: { label: 'Filed', tone: 'green' },
  accepted: { label: 'Accepted', tone: 'green' },
}

const STATUS_ORDER: ReturnStatus[] = ['not-started', 'in-progress', 'extension-filed', 'filed', 'accepted']

function blankEntity(): EntityInput {
  return {
    name: '',
    shortName: '',
    state: 'TX',
    classification: 'disregarded',
    members: [],
    description: '',
    icon: '🏛️',
    ein: '',
    formedOn: null,
    registeredAgent: '',
  }
}

function AddEntityForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<EntityInput>(blankEntity())
  const [membersText, setMembersText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('The legal name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await createEntity({
        ...form,
        members: membersText.split(',').map((m) => m.trim()).filter(Boolean),
      })
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the business')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-slate-100/60 dark:bg-slate-900/60 rounded-xl">
      <input
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Legal name (e.g. Acme Holdings LLC)"
        className={`${inputClass} sm:col-span-2`}
      />
      <input
        value={form.shortName}
        onChange={(e) => setForm({ ...form, shortName: e.target.value })}
        placeholder="Short name"
        className={inputClass}
      />
      <input
        value={form.icon}
        onChange={(e) => setForm({ ...form, icon: e.target.value })}
        maxLength={4}
        placeholder="Icon"
        className={inputClass}
      />
      <select
        value={form.classification}
        onChange={(e) => setForm({ ...form, classification: e.target.value as EntityClassification })}
        className={`${inputClass} sm:col-span-2`}
      >
        {(Object.keys(CLASSIFICATION_LABELS) as EntityClassification[]).map((c) => (
          <option key={c} value={c}>{CLASSIFICATION_LABELS[c]}</option>
        ))}
      </select>
      <input
        value={form.state}
        onChange={(e) => setForm({ ...form, state: e.target.value })}
        maxLength={2}
        placeholder="State"
        className={inputClass}
      />
      <input
        value={form.ein}
        onChange={(e) => setForm({ ...form, ein: e.target.value })}
        placeholder="EIN (optional)"
        className={inputClass}
      />
      <input
        value={membersText}
        onChange={(e) => setMembersText(e.target.value)}
        placeholder="Members / owners, comma-separated"
        className={`${inputClass} sm:col-span-2`}
      />
      <input
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="What this business does"
        className={`${inputClass} sm:col-span-2`}
      />
      {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-2 lg:col-span-4">{error}</p>}
      <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between gap-3">
        <p className="text-slate-500 text-xs">
          You can add P&Ls, 1099s, records, and documents right after it's created.
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={busy} className={primaryBtn}>
            {busy ? 'Adding…' : 'Add business'}
          </button>
        </div>
      </div>
    </form>
  )
}

function EntityCard({
  entity,
  pnls,
  docCount,
  recordCount,
  onOpen,
  onClassificationChange,
}: {
  entity: BusinessEntity
  pnls: EntityPnL[]
  docCount: number
  recordCount: number
  onOpen: () => void
  onClassificationChange: (id: string, c: EntityClassification) => void
}) {
  const mine = pnls.filter((p) => p.entityId === entity.id).sort((a, b) => b.year - a.year)
  const [year, setYear] = useState(mine[0]?.year ?? new Date().getFullYear())
  const [editingClass, setEditingClass] = useState(false)
  const pnl = mine.find((p) => p.year === year)
  const totalExpenses = pnl ? pnl.expenses.reduce((s, e) => s + e.amount, 0) : 0

  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span className="text-2xl">{entity.icon}</span>
          <div>
            <p className="text-slate-900 dark:text-white font-semibold text-sm">{entity.name}</p>
            <p className="text-slate-500 text-xs">
              {entity.state}
              {entity.members.length > 0 && ` · ${entity.members.join(', ')}`}
            </p>
          </div>
        </div>
        {mine.length > 0 && (
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 text-xs rounded-lg px-2 py-1 border border-slate-300 dark:border-slate-600 outline-none"
          >
            {mine.map((p) => (
              <option key={p.year} value={p.year}>{p.year === new Date().getFullYear() ? `${p.year} YTD` : String(p.year)}</option>
            ))}
          </select>
        )}
      </div>

      <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">{entity.description}</p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {editingClass ? (
          <select
            autoFocus
            value={entity.classification}
            onChange={(e) => {
              onClassificationChange(entity.id, e.target.value as EntityClassification)
              setEditingClass(false)
            }}
            onBlur={() => setEditingClass(false)}
            className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs rounded-lg px-2 py-1 border border-blue-500 outline-none"
          >
            {(Object.keys(CLASSIFICATION_LABELS) as EntityClassification[]).map((c) => (
              <option key={c} value={c}>{CLASSIFICATION_LABELS[c]}</option>
            ))}
          </select>
        ) : (
          <button onClick={() => setEditingClass(true)} title="Click to change tax classification">
            <Chip tone="blue">{CLASSIFICATION_LABELS[entity.classification]} ✎</Chip>
          </button>
        )}
        {entity.classificationAssumed && <Chip tone="amber">assumed — confirm</Chip>}
      </div>

      {pnl ? (
        <div>
          <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-200/50 dark:border-slate-700/50">
            <span className="text-slate-600 dark:text-slate-300">Revenue</span>
            <span className="text-slate-900 dark:text-white font-medium">{money(pnl.revenue)}</span>
          </div>
          {pnl.expenses.map((e) => (
            <div key={e.category} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-200/50 dark:border-slate-700/50">
              <span className="text-slate-500 pl-3">{e.category}</span>
              <span className="text-slate-500 dark:text-slate-400">−{money(e.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-sm py-2">
            <span className="text-slate-800 dark:text-slate-200 font-medium">
              Net income {year === new Date().getFullYear() ? '(YTD)' : ''}
            </span>
            <span className={`font-bold ${pnl.netIncome >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {signedMoney(pnl.netIncome)}
            </span>
          </div>
          <p className="text-slate-400 dark:text-slate-600 text-xs">
            Through {shortDate(pnl.through)} · margin{' '}
            {pnl.revenue > 0 ? `${Math.round((pnl.netIncome / pnl.revenue) * 100)}%` : '—'} · expenses {money(totalExpenses)}
          </p>
        </div>
      ) : (
        <p className="text-slate-500 text-sm">No P&L recorded for {year}.</p>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
        <p className="text-slate-500 text-xs">
          📄 {docCount} document{docCount === 1 ? '' : 's'} · 🗒️ {recordCount} record{recordCount === 1 ? '' : 's'}
        </p>
        <button onClick={onOpen} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium">
          Edit / manage →
        </button>
      </div>
    </Card>
  )
}

export default function EntitiesTab() {
  const [entities, setEntities] = useState<BusinessEntity[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [pnls, setPnls] = useState<EntityPnL[]>([])
  const [forms, setForms] = useState<Form1099Record[]>([])
  const [schedule, setSchedule] = useState<ReturnScheduleItem[]>([])
  const [docs, setDocs] = useState<FinanceDocument[]>([])
  const [recordCounts, setRecordCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const reload = useCallback(async () => {
    try {
    const [e, a, p, f, s, d, r] = await Promise.all([
      listEntities(),
      listAccounts(),
      listPnL(),
      list1099s(),
      listReturnSchedule(),
      listDocuments(),
      listEntityRecords(),
    ])
    setEntities(e)
    setAccounts(a)
    setPnls(p)
    setForms(f)
    setSchedule(s)
    setDocs(d)
    setRecordCounts(
      r.reduce<Record<string, number>>((acc, record) => {
        acc[record.entityId] = (acc[record.entityId] ?? 0) + 1
        return acc
      }, {}),
    )
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  async function handleClassification(id: string, c: EntityClassification) {
    await updateEntityClassification(id, c)
    await reload()
  }

  async function advanceStatus(item: ReturnScheduleItem) {
    const idx = STATUS_ORDER.indexOf(item.status)
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length]
    setBusy(item.id)
    try {
      await updateReturnStatus(item.id, next)
      await reload()
    } finally {
      setBusy(null)
    }
  }

  const entityName = useMemo(() => {
    const map = new Map(entities.map((e) => [e.id, e.shortName]))
    return (id: string) => map.get(id) || id
  }, [entities])

  if (loadError) return <LoadError error={loadError} onRetry={reload} />
  if (loading) return <LoadingRows rows={6} />

  const selected = entities.find((e) => e.id === selectedId) ?? null
  if (selected) {
    return (
      <EntityWorkspace
        entity={selected}
        entities={entities}
        accounts={accounts}
        onChanged={reload}
        onBack={() => {
          setSelectedId(null)
          reload()
        }}
      />
    )
  }

  const needsW9 = forms.filter((f) => f.status === 'needs-w9')
  const openReturns = schedule.filter((r) => !['filed', 'accepted'].includes(r.status))

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {(needsW9.length > 0 || openReturns.length > 0) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 space-y-1">
          {openReturns.map((r) => {
            const due = r.extendedDueDate || r.dueDate
            const days = daysUntil(due)
            return (
              <p key={r.id} className="text-amber-600 dark:text-amber-400 text-sm">
                📅 <span className="font-medium">{r.entityName}</span> — {r.form} due {shortDate(due)}
                {days >= 0 ? ` (${days} days)` : ' (past due)'}
              </p>
            )
          })}
          {needsW9.map((f) => (
            <p key={f.id} className="text-amber-600 dark:text-amber-400 text-sm">
              📋 <span className="font-medium">{entityName(f.entityId)}</span> — W-9 needed from {f.counterparty} before
              filing their 1099-{f.type.split('-')[1]}
            </p>
          ))}
        </div>
      )}

      {/* Entity roster */}
      <Card
        title="Businesses"
        subtitle="Open one to edit its details and add documents, P&Ls, 1099s, and records"
        action={
          <button onClick={() => setAdding((v) => !v)} className={primaryBtn}>
            {adding ? 'Close' : '+ Add business'}
          </button>
        }
      >
        {adding && (
          <div className="mb-4">
            <AddEntityForm
              onCreated={() => {
                setAdding(false)
                reload()
              }}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {entities.map((e) => (
            <EntityCard
              key={e.id}
              entity={e}
              pnls={pnls}
              docCount={docs.filter((d) => d.entityId === e.id).length}
              recordCount={recordCounts[e.id] ?? 0}
              onOpen={() => setSelectedId(e.id)}
              onClassificationChange={handleClassification}
            />
          ))}
        </div>
      </Card>

      {/* Return schedule */}
      <Card title="Return Schedule — Federal & State" subtitle="Click a status chip to advance it">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 pr-3 font-medium">Filer</th>
                <th className="text-left py-2 pr-3 font-medium">Form</th>
                <th className="text-left py-2 pr-3 font-medium hidden sm:table-cell">Jurisdiction</th>
                <th className="text-left py-2 pr-3 font-medium">Due</th>
                <th className="text-left py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((r) => {
                const meta = RETURN_STATUS_META[r.status]
                const effectiveDue = r.extendedDueDate || r.dueDate
                return (
                  <tr key={r.id} className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                    <td className="py-2.5 pr-3 text-slate-800 dark:text-slate-200">{r.entityName}</td>
                    <td className="py-2.5 pr-3">
                      <p className="text-slate-900 dark:text-white">{r.form}</p>
                      {r.notes && <p className="text-slate-500 text-xs">{r.notes}</p>}
                    </td>
                    <td className="py-2.5 pr-3 hidden sm:table-cell">
                      <Chip tone={r.jurisdiction === 'Federal' ? 'blue' : 'purple'}>{r.jurisdiction}</Chip>
                    </td>
                    <td className="py-2.5 pr-3">
                      <p className="text-slate-800 dark:text-slate-200">{shortDate(effectiveDue)}</p>
                      {r.extendedDueDate && (
                        <p className="text-slate-500 text-xs">orig. {shortDate(r.dueDate)}</p>
                      )}
                    </td>
                    <td className="py-2.5">
                      <button onClick={() => advanceStatus(r)} disabled={busy === r.id} title="Click to advance status">
                        <Chip tone={meta.tone}>{meta.label} ↻</Chip>
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 1099s */}
      <Card
        title="1099s — Issued & Received"
        subtitle="All entities; open a business to add or edit its records"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2 pr-3 font-medium">Entity</th>
                <th className="text-left py-2 pr-3 font-medium">Form</th>
                <th className="text-left py-2 pr-3 font-medium">Counterparty</th>
                <th className="text-right py-2 pr-3 font-medium">Amount</th>
                <th className="text-left py-2 pr-3 font-medium hidden sm:table-cell">TY</th>
                <th className="text-left py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id} className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
                  <td className="py-2.5 pr-3 text-slate-800 dark:text-slate-200">{entityName(f.entityId)}</td>
                  <td className="py-2.5 pr-3">
                    <p className="text-slate-900 dark:text-white">{f.type}</p>
                    <p className="text-slate-500 text-xs">{f.direction}</p>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{f.counterparty}</td>
                  <td className="py-2.5 pr-3 text-right text-slate-900 dark:text-white font-medium">{money(f.amount)}</td>
                  <td className="py-2.5 pr-3 text-slate-500 dark:text-slate-400 hidden sm:table-cell">{f.taxYear}</td>
                  <td className="py-2.5">
                    <Chip
                      tone={
                        f.status === 'needs-w9' ? 'red' : f.status === 'draft' ? 'amber' : f.status === 'filed' ? 'green' : 'slate'
                      }
                    >
                      {f.status}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
